import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext";

export const DEFAULT_LOGO_HEIGHT = 52;

const GREEN = "#7DD856";
// Geometry lifted straight from the logo artwork (2000×800): ring centre (320, 454),
// outer radius 300, stroke 126, wordmark starting at x=642, artwork height 720.
const RING = 0.833;   // ring diameter as a share of the lockup height
const RING_TOP = 0.158;
const WORD_X = 0.864;
const WIDTH = 2.718;  // full lockup width / height

/**
 * The header lockup with a live ring: only the circular "Re" turns as the page scrolls,
 * the wordmark stays put. Colours follow the theme, exactly like the flat logo did.
 */
export const BrandRingLogo = ({ height = 52, className = "", strength = 0.2 }) => {
    const ctx = useTheme();
    const variant = (ctx?.theme || "dark") === "light" ? "dark" : "light";
    const track = variant === "dark" ? "#3F3F3F" : "#FFFFFF";
    const arc = useRef(null);
    const [small, setSmall] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const sync = () => setSmall(mq.matches);
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        let frame = 0;
        const paint = () => {
            if (arc.current) arc.current.style.transform = `rotate(${-104 + window.scrollY * strength}deg)`;
        };
        const onScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(paint);
        };
        paint();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(frame); };
    }, [strength]);

    const box = small ? Math.round(height * 0.82) : height;
    const ring = box * RING;
    return (
        <div className={`relative shrink-0 ${className}`} data-testid="brand-ring-logo"
            style={{ height: box, width: box * WIDTH }} aria-label="ReHealthMe" role="img">
            <div className="absolute" style={{ top: box * RING_TOP, left: 0, width: ring, height: ring }}>
                <svg viewBox="0 0 600 600" width={ring} height={ring} className="block">
                    <circle cx="300" cy="300" r="237" fill="none" stroke={track} strokeWidth="126" />
                    <g ref={arc} className="logo-ring__arc is-settled" style={{ transformOrigin: "300px 300px" }}>
                        <circle cx="300" cy="300" r="237" fill="none" stroke={GREEN} strokeWidth="126"
                            strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 237 * (262 / 360)} 9999`} />
                    </g>
                </svg>
                <img src={`/brand/re-${variant === "dark" ? "dark" : "light"}.png`} alt=""
                    data-testid="brand-ring-glyph"
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    style={{ width: ring * 0.58, height: ring * 0.58, objectFit: "contain" }} />
            </div>
            <img src={`/brand/wordmark-${variant === "dark" ? "dark" : "light"}.png`} alt="ReHealthMe"
                data-testid="brand-ring-wordmark" className="absolute top-0"
                style={{ left: box * WORD_X, height: box, width: "auto" }} />
        </div>
    );
};

export default BrandRingLogo;
